import { useAtom } from "jotai";
import { tourAtom } from "../atoms/tourAtom";
import { useMantineColorScheme } from "@mantine/core";

export const useTour = () => {
  const [tour, setTour] = useAtom(tourAtom);
  // Prevent concurrent tours
  let isRunning = false;
  // Read Mantine color scheme (light/dark)
  const { colorScheme } = useMantineColorScheme();

  const startTour = () => {
    if (!tour) return;
    // If an overlay already exists or tour is flagged running, do nothing
    if (isRunning) return;
    const existingOverlay = document.querySelector('.tour-overlay');
    if (existingOverlay) return;
    isRunning = true;

    // Add data-intro elements already prepared via addDataIntroByXPath
    const steps = document.querySelectorAll('[data-intro]');
    if (!steps.length) return;

    const mantinePaperRoot = document.querySelector('.mantine-Paper-root');
    let nextDiv; let lastDiv;
    if (mantinePaperRoot) {
      nextDiv = mantinePaperRoot?.children[0];
      lastDiv = mantinePaperRoot?.children[mantinePaperRoot?.children.length - 1];
      if (nextDiv && nextDiv.tagName === 'DIV') {
        nextDiv.style.zIndex = 'auto';
        lastDiv.style.zIndex = 'auto';
      }
    }

    const overlay = document.createElement('div');
    overlay.className = 'tour-overlay';
    overlay.style.opacity = '0';
    overlay.style.background = 'rgba(0,0,0,0.78)'; // ensure darker backdrop
    overlay.style.display = 'block'; // force visible even if CSS default is none
    document.body.appendChild(overlay);
    overlay.style.pointerEvents = 'auto';

    const texts = []; // keep for cleanup compatibility
    let currentIndex = 0;
    let currentTextEl = null;
    let currentListeners = [];

    // Original dynamic CSS (kept) so existing look remains
    const addDynamicCSS = () => {
      if (!document.getElementById('tour-dynamic-style')) {
        const style = document.createElement('style');
        style.id = 'tour-dynamic-style';
        style.innerHTML = `
          .tour-next-btn { cursor:pointer; margin-top:8px; display:inline-block; background:#0078d4; color:#fff; border:none; padding:4px 10px; border-radius:4px; font-size:13px; }
          .tour-text { position:fixed; }
          .tour-highlight-light { color: inherit !important; }
          // .tour-text::after { content:''; position:absolute; width:0; height:0; }
          // .tour-text.top::after, .tour-text.top-left::after { bottom:-8px; left:50%; transform:translateX(-50%); border-width:8px 8px 0 8px; border-style:solid; border-color:#1d2733 transparent transparent transparent; }
          // .tour-text.bottom::after, .tour-text.bottom-left::after { top:-8px; left:50%; transform:translateX(-50%); border-width:0 8px 8px 8px; border-style:solid; border-color:transparent transparent #1d2733 transparent; }
          // .tour-text.right::after { left:-8px; top:50%; transform:translateY(-50%); border-width:8px 8px 8px 0; border-style:solid; border-color:transparent #1d2733 transparent transparent; }
          // .tour-text.left::after { right:-8px; top:50%; transform:translateY(-50%); border-width:8px 0 8px 8px; border-style:solid; border-color:transparent transparent transparent #1d2733; }
        `;
        document.head.appendChild(style);
      }
    };
    addDynamicCSS();

    const cleanupCurrent = () => {
      if (currentTextEl) {
        currentTextEl.classList.remove('show');
        const el = currentTextEl;
        setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
        currentTextEl = null;
      }
      steps.forEach(s => {
        if (s.classList.contains('tour-highlight')) {
          s.classList.remove('tour-highlight');
        }
        if (s.classList.contains('tour-highlight-light')) {
          s.classList.remove('tour-highlight-light');
        }
        // Restore original z-index/position if stored
        if (s.dataset.tourOrigZ !== undefined) {
          if (s.dataset.tourOrigZ === '') s.style.removeProperty('z-index'); else s.style.zIndex = s.dataset.tourOrigZ;
          delete s.dataset.tourOrigZ;
        }
        if (s.dataset.tourOrigPos !== undefined) {
          if (s.dataset.tourOrigPos === '') s.style.removeProperty('position'); else s.style.position = s.dataset.tourOrigPos;
          delete s.dataset.tourOrigPos;
        }
        // Restore original background styles
        if (s.dataset.tourOrigBg !== undefined) {
          if (s.dataset.tourOrigBg === '') s.style.removeProperty('background'); else s.style.background = s.dataset.tourOrigBg;
          delete s.dataset.tourOrigBg;
        }
        if (s.dataset.tourOrigBgColor !== undefined) {
          if (s.dataset.tourOrigBgColor === '') s.style.removeProperty('background-color'); else s.style.backgroundColor = s.dataset.tourOrigBgColor;
          delete s.dataset.tourOrigBgColor;
        }
        // Restore original text color
        if (s.dataset.tourOrigColor !== undefined) {
          if (s.dataset.tourOrigColor === '') s.style.removeProperty('color'); else s.style.color = s.dataset.tourOrigColor;
          delete s.dataset.tourOrigColor;
        }
      });
      currentListeners.forEach(({ type, fn }) => window.removeEventListener(type, fn));
      currentListeners = [];
    };

    const positionText = (stepEl, textEl) => {
      const position = stepEl.getAttribute('data-position') || 'top';
      const rect = stepEl.getBoundingClientRect();
      const margin = 20;
      switch (position) {
        case 'top':
          textEl.style.left = `${rect.left + rect.width / 2 - textEl.offsetWidth / 2}px`;
          textEl.style.top = `${rect.top - textEl.offsetHeight - margin}px`; break;
        case 'right':
          textEl.style.left = `${rect.right + 20}px`;
          textEl.style.top = `${rect.top + rect.height / 2 - textEl.offsetHeight / 2}px`; break;
        case 'bottom':
          textEl.style.left = `${rect.left + rect.width / 2 - textEl.offsetWidth / 2}px`;
          textEl.style.top = `${rect.bottom + 20}px`; break;
        case 'left':
          textEl.style.left = `${rect.left - textEl.offsetWidth - 20}px`;
          textEl.style.top = `${rect.top + rect.height / 2 - textEl.offsetHeight / 2}px`; break;
        case 'top-left':
          textEl.style.left = `${rect.left - 90 + rect.width / 2 - textEl.offsetWidth / 2}px`;
          textEl.style.top = `${rect.top - textEl.offsetHeight - 20}px`; break;
        case 'bottom-left':
          textEl.style.left = `${rect.left + rect.width / 2 - textEl.offsetWidth / 2}px`;
          textEl.style.top = `${rect.bottom + 10}px`; break;
        default:
          textEl.style.left = `${rect.left + rect.width / 2 - textEl.offsetWidth / 2}px`;
          textEl.style.top = `${rect.top - textEl.offsetHeight - 20}px`; break;
      }
      // viewport adjust
      const vw = window.innerWidth; const vh = window.innerHeight; 
      const tRect = textEl.getBoundingClientRect();
      if (tRect.left < 8) textEl.style.left = '8px';
      if (tRect.right > vw - 8) textEl.style.left = `${vw - tRect.width - 8}px`;
      if (tRect.top < 8) textEl.style.top = `${rect.bottom + 20}px`;
      if (tRect.bottom > vh - 8) textEl.style.top = `${rect.top - tRect.height - 20}px`;
    };

    const showStep = (index) => {
      if (index < 0 || index >= steps.length) return finish();
      cleanupCurrent();
      currentIndex = index;
      const stepEl = steps[index];
  // Force reflow to ensure sticky layout applied before measuring
  void stepEl.offsetHeight;
  // Preserve original styles for restoration
  if (stepEl.dataset.tourOrigZ === undefined) stepEl.dataset.tourOrigZ = stepEl.style.zIndex || '';
  if (stepEl.dataset.tourOrigPos === undefined) stepEl.dataset.tourOrigPos = stepEl.style.position || '';
  const cs = window.getComputedStyle(stepEl);
  if (cs.position === 'static') stepEl.style.position = 'relative';
  stepEl.style.zIndex = '99999';
  stepEl.classList.add('tour-highlight');
  // In light mode, apply special highlight class that does not enforce color
  if (colorScheme !== 'dark') {
    stepEl.classList.add('tour-highlight-light');
  }

      const textEl = document.createElement('div');
  textEl.className = 'tour-text';
  textEl.style.pointerEvents = 'auto';
  textEl.style.display = 'flex';
  textEl.style.flexDirection = 'column';
  textEl.style.alignItems = 'stretch';
  textEl.style.justifyContent = 'flex-start';
  textEl.style.minHeight = '120px';
  textEl.style.boxSizing = 'border-box';
  textEl.style.background = '#fff';
  textEl.style.borderRadius = '12px';
  textEl.style.boxShadow = '0 4px 24px rgba(0,0,0,0.18)';
  textEl.style.padding = '24px 32px 32px 32px';
  textEl.style.maxWidth = '340px';
  textEl.style.border = '1.5px solid #e0e0e0';
      const id = `tour-text-${index}`;
      textEl.id = id;
      const positionClass = stepEl.getAttribute('data-position') || 'top';
      textEl.classList.add(positionClass);

      const messageWrapper = document.createElement('div');
      messageWrapper.innerHTML = stepEl.getAttribute('data-intro') || '';
      messageWrapper.style.textAlign = 'center';
      messageWrapper.style.width = '100%';

      // Controls wrapper
      // Detect dark mode from body or html class
      const isDark = document.body.classList.contains('dark') || document.documentElement.classList.contains('dark');

      // X button (close)
      const closeBtn = document.createElement('button');
      closeBtn.textContent = '✕';
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '8px';
      closeBtn.style.right = '12px';
      closeBtn.style.background = 'none';
      closeBtn.style.border = 'none';
      closeBtn.style.fontSize = '14px';
      closeBtn.style.cursor = 'pointer';
      closeBtn.style.color = isDark ? '#eee' : '#888';
      closeBtn.onclick = (e) => { e.stopPropagation(); finish(); };

      // Prev button
      const prevBtn = document.createElement('button');
      prevBtn.className = 'tour-next-btn';
      prevBtn.textContent = 'Prev';
      prevBtn.style.position = 'absolute';
      prevBtn.style.left = '24px';
      prevBtn.style.bottom = '15px';
      prevBtn.style.background = isDark ? '#444' : '#e0e0e0';
      prevBtn.style.color = isDark ? '#fff' : '#222';
      prevBtn.style.borderRadius = '6px';
      prevBtn.style.padding = '6px 18px';
      prevBtn.style.fontSize = '15px';
      prevBtn.style.border = 'none';
      prevBtn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      prevBtn.style.cursor = index === 0 ? 'not-allowed' : 'pointer';
      prevBtn.disabled = index === 0;
      // Make Prev button blue when enabled
      if (!prevBtn.disabled) {
        prevBtn.style.background = '#0078d4';
        prevBtn.style.color = '#fff';
      }
      prevBtn.onclick = (e) => {
        e.stopPropagation();
        if (currentIndex > 0) showStep(currentIndex - 1);
      };

      // Next / Finish button
      const nextBtn = document.createElement('button');
      nextBtn.className = 'tour-next-btn';
      nextBtn.textContent = index === steps.length - 1 ? 'Finish' : 'Next';
      nextBtn.style.position = 'absolute';
      nextBtn.style.right = '24px';
      nextBtn.style.bottom = '15px';
      nextBtn.style.background = '#0078d4';
      nextBtn.style.color = '#fff';
      nextBtn.style.borderRadius = '6px';
      nextBtn.style.padding = '6px 18px';
      nextBtn.style.fontSize = '15px';
      nextBtn.style.border = 'none';
      nextBtn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      nextBtn.style.cursor = 'pointer';
      nextBtn.onclick = (e) => {
        e.stopPropagation();
        if (currentIndex === steps.length - 1) finish(); else showStep(currentIndex + 1);
      };

      // Card dark mode styles
      if (isDark) {
        textEl.style.background = '#222';
        textEl.style.color = '#111';
        textEl.style.border = '1.5px solid #444';
        textEl.style.boxShadow = '0 4px 24px rgba(0,0,0,0.38)';
      } else {
        textEl.style.color = '#111';
      }

      textEl.appendChild(closeBtn);
      textEl.appendChild(messageWrapper);
      textEl.appendChild(prevBtn);
      textEl.appendChild(nextBtn);

      document.body.appendChild(textEl);

      const updatePos = () => positionText(stepEl, textEl);
      updatePos();
      // Multi-frame + timed repositions to handle initial sticky jump
      const scheduleRepositions = () => {
        let frames = 0;
        const rafLoop = () => {
          updatePos();
          if (frames++ < 8) requestAnimationFrame(rafLoop);
        };
        requestAnimationFrame(rafLoop);
        setTimeout(updatePos, 60);
        setTimeout(updatePos, 120);
        setTimeout(updatePos, 240);
        setTimeout(updatePos, 400);
      };
      window.addEventListener('scroll', updatePos);
      window.addEventListener('resize', updatePos);
      currentListeners.push({ type: 'scroll', fn: updatePos }, { type: 'resize', fn: updatePos });

      requestAnimationFrame(() => textEl.classList.add('show'));
      currentTextEl = textEl;
      // Smooth scroll to center; wait for scroll to settle before final positioning boost
      let scrolled = false;
      const finalize = () => { if (!scrolled) { scrolled = true; scheduleRepositions(); } };
      stepEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      // Fallback timers if no observer events fire
      setTimeout(finalize, 350);
      setTimeout(finalize, 700);
      // Try IntersectionObserver to detect when element is centered / sticky applied
      try {
        const io = new IntersectionObserver((entries) => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              finalize();
              io.disconnect();
            }
          });
        }, { threshold: 0.6 });
        io.observe(stepEl);
      } catch(_) { /* ignore */ }
    };

    const finish = () => {
      cleanupCurrent();
      overlay.classList.remove('show');
      setTimeout(() => {
        if (nextDiv && nextDiv.tagName === 'DIV') {
          nextDiv.style.zIndex = '3';
          if (lastDiv) lastDiv.style.zIndex = '3';
        }
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        setTour(false);
        isRunning = false;
        const seApp = document.querySelector('se-app');
        if (seApp && seApp.style) seApp.style.position = 'fixed';
      }, 200);
    };

    overlay.onclick = finish; // clicking outside closes tour

    // Fade in overlay then first step
    let opacity = 0;
    const animateOverlay = () => {
      opacity += 0.05;
      if (opacity <= 1) { overlay.style.opacity = opacity.toString(); requestAnimationFrame(animateOverlay); }
      else {
        overlay.style.opacity = '1';
        // Defer first step one extra frame so any sticky container paint completes
        requestAnimationFrame(() => showStep(0));
      }
    };
    requestAnimationFrame(animateOverlay);
  };

  return { startTour, setTour };
};

export const addDataIntroByXPath = (xpath, dataIntro, dataPosition = 'top', style = {}) => {
  const element = document.evaluate(
    xpath,
    document,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  ).singleNodeValue;
  if (element) {
    element.setAttribute('data-intro', dataIntro);
    element.setAttribute('data-position', dataPosition);
    if (style) {
      Object.keys(style).forEach((key) => { element.style[key] = style[key]; });
    }
  }
};
